// AGC: Tournament Lobby Component
// AGC: Displays instant tournament information, player list, countdown, and rewards
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Trophy, Users, Award, Play } from 'lucide-react'
import { useWebSocket } from '../../hooks/useWebSocket'
import { usePlayerProgression } from '../../hooks/usePlayerProgression'
import { useBlockchainStats } from '../../hooks/useBlockchainStats'
import { useTournamentPayment } from '../../hooks/useTournamentPayment'
import { useAccount } from 'wagmi'
import { TournamentNFTSelection } from './TournamentNFTSelection'
import { resolveAssetPath } from '../../utils/audio'

interface Tournament {
  id: string
  status: 'WAITING_FOR_PLAYERS' | 'COUNTDOWN' | 'RUNNING' | 'FINISHED'
  minPlayersToStart: number
  maxPlayers: number
  createdAt: number
  autoStartAt: number
  countdownEndsAt?: number
  currentRound?: number // AGC: Current round number (1-based, 0 means not started)
  rewards: {
    first: { ogb: number; potions: number; nftItem?: string }
    second: { ogb: number; potions: number }
    third: { ogb: number; potions: number }
  }
}

interface Participant {
  playerId: string
  level: number
  totalBattles: number
  winRate: number
  joinedAt: number
  isReady: boolean
  isActive: boolean
}

interface FinalStanding {
  address: string
  wins: number
  losses: number
  draws: number
}

interface TournamentRound {
  round: number
  matches: string[][] // Array of [player1, player2] pairs
}

interface TournamentLobbyProps {
  onNavigateToPage?: (page: 'p2pBattle' | 'home' | 'games' | 'tournamentMatch') => void
  tournamentId?: string | null // AGC: Optional tournament ID to show specific tournament
}

export const TournamentLobby: React.FC<TournamentLobbyProps> = ({ onNavigateToPage, tournamentId: propTournamentId }) => {
  const [tournament, setTournament] = useState<Tournament | null>(null)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [finalStandings, setFinalStandings] = useState<FinalStanding[] | null>(null)
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle')
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [tournamentRounds, setTournamentRounds] = useState<TournamentRound[]>([])
  const [currentRound, setCurrentRound] = useState<number>(0)
  const [showNFTSelection, setShowNFTSelection] = useState(false)
  const [selectedMatch, setSelectedMatch] = useState<{ opponentAddress: string; round: number; matchNumber: number } | null>(null)
  const [copiedTournamentId, setCopiedTournamentId] = useState(false)
  const [completedMatches, setCompletedMatches] = useState<Record<string, boolean>>({})
  const [matchResults, setMatchResults] = useState<Record<string, { winner: string; loser: string; isDraw?: boolean }>>({})
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const { address } = useAccount()
  const { playerStats } = usePlayerProgression(address || '')
  const { stats: blockchainStats } = useBlockchainStats(address || '')
  const { socket } = useWebSocket()
  const { payEntry, isLoading: isPaymentLoading, error: paymentErrorHook } = useTournamentPayment()
  const tournamentRef = useRef<Tournament | null>(null)
  const participantsRef = useRef<Participant[]>([])
  const countdownAudioRef = useRef<HTMLAudioElement | null>(null)
  const prevStatusRef = useRef<Tournament['status'] | null>(null)
  const prevRoundRef = useRef<number>(0) // AGC: Track previous round to prevent infinite loop
  const lastRoundRequestRef = useRef<number>(0) // AGC: Debounce round requests

  const isPlayerInCurrentTournament = useCallback(() => {
    if (!address || !tournamentRef.current) {
      return false
    }
    const playerLower = address.toLowerCase()
    return participantsRef.current.some((p) => p.playerId.toLowerCase() === playerLower)
  }, [address])

  const shouldIgnoreIncomingTournament = useCallback(
    (incomingTournamentId: string) => {
      const current = tournamentRef.current
      if (!current) {
        return false
      }
      if (current.id === incomingTournamentId) {
        return false
      }
      const isLockedStatus = current.status === 'COUNTDOWN' || current.status === 'RUNNING'
      if (!isLockedStatus) {
        return false
      }
      if (!isPlayerInCurrentTournament()) {
        return false
      }
      console.log('⏸️ AGC: Ignoring tournament event while active tournament is running', {
        activeId: current.id,
        incomingId: incomingTournamentId,
        status: current.status,
      })
      return true
    },
    [isPlayerInCurrentTournament],
  )

  const updateTournamentState = useCallback((nextTournament: Tournament | null) => {
    tournamentRef.current = nextTournament
    setTournament(nextTournament)
    // AGC: Sync currentRound from tournament object (source of truth)
    if (nextTournament?.currentRound !== undefined) {
      setCurrentRound(nextTournament.currentRound)
      console.log('🏆 TournamentLobby: Synced currentRound from tournament object:', nextTournament.currentRound)
    }
  }, [])

  const updateParticipantsState = useCallback((nextParticipants: Participant[]) => {
    participantsRef.current = nextParticipants
    setParticipants(nextParticipants)
  }, [])

  const shouldApplyTournamentUpdate = useCallback(
    (incomingTournament: Tournament, incomingParticipants: Participant[] = []) => {
      const currentTournament = tournamentRef.current
      if (!currentTournament) {
        return true
      }

      if (incomingTournament.id === currentTournament.id) {
        return true
      }

      const playerLower = address?.toLowerCase()
      const playerInCurrent =
        !!playerLower && participantsRef.current.some((p) => p.playerId.toLowerCase() === playerLower)
      const playerInIncoming =
        !!playerLower && incomingParticipants.some((p) => p.playerId.toLowerCase() === playerLower)

      if (playerInIncoming) {
        console.log('🏆 TournamentLobby: Switching to tournament that current player joined', incomingTournament.id)
        return true
      }

      if (playerInCurrent && currentTournament.status !== 'FINISHED') {
        console.log('⏸️ AGC: Ignoring new tournament update while current tournament is active for this player', {
          currentTournamentId: currentTournament.id,
          incomingTournamentId: incomingTournament.id,
          currentStatus: currentTournament.status,
        })
        return false
      }

      return true
    },
    [address],
  )

  useEffect(() => {
    // AGC: Preload countdown audio cue
    countdownAudioRef.current = new Audio(resolveAssetPath('/sounds/match-found.mp3'))
    if (countdownAudioRef.current) {
      countdownAudioRef.current.volume = 0.7
    }
  }, [])

  useEffect(() => {
    setCompletedMatches({})
  }, [tournament?.id])

  useEffect(() => {
    // AGC: Keep local countdown synced with latest tournament phase
    if (!tournament) {
      setTimeRemaining(0)
      return
    }

    const updateTimeRemaining = () => {
      if (tournament.status === 'WAITING_FOR_PLAYERS') {
        const target = tournament.autoStartAt || 0
        setTimeRemaining(target > 0 ? Math.max(0, target - Date.now()) : 0)
      } else if (tournament.status === 'COUNTDOWN') {
        const target = tournament.countdownEndsAt || 0
        setTimeRemaining(target > 0 ? Math.max(0, target - Date.now()) : 0)
      } else {
        setTimeRemaining(0)
      }
    }

    updateTimeRemaining()
    const interval = window.setInterval(updateTimeRemaining, 1000)
    return () => window.clearInterval(interval)
  }, [tournament])
  
  // AGC: Listen for tournament events
  useEffect(() => {
    if (!socket) return
    
    const handleTournamentCreated = (data: Tournament) => {
      console.log('🏆 Tournament created:', data)
      if (shouldIgnoreIncomingTournament(data.id)) {
        return
      }
      if (!shouldApplyTournamentUpdate(data, [])) {
        return
      }
      updateTournamentState(data)
      updateParticipantsState([])
    }

    const handleTournamentUpdated = (data: { tournament: Tournament; participants: Participant[]; readyCount?: number }) => {
      console.log('🏆 Tournament updated:', data)
      if (shouldIgnoreIncomingTournament(data.tournament.id)) {
        return
      }
      if (!shouldApplyTournamentUpdate(data.tournament, data.participants)) {
        return
      }
      updateTournamentState(data.tournament)
      updateParticipantsState(data.participants)
    }

    const handleTournamentReconnect = (data: { tournament: Tournament; participants: Participant[]; playerCount: number }) => {
      console.log('🏆 Tournament reconnected:', data)
      if (shouldIgnoreIncomingTournament(data.tournament.id)) {
        return
      }
      if (!shouldApplyTournamentUpdate(data.tournament, data.participants)) {
        return
      }
      updateTournamentState(data.tournament)
      updateParticipantsState(data.participants)
    }

    const handleNoActiveTournament = () => {
      console.log('🏆 No active tournament to reconnect to')
      updateTournamentState(null)
      updateParticipantsState([])
    }
    
    const handleTournamentFinished = (data: { tournamentId: string; finalStandings: FinalStanding[]; top3: { first: string; second: string; third: string } }) => {
      console.log('🏆 Tournament finished!', data)
      setFinalStandings(data.finalStandings)
      // AGC: Update tournament status to FINISHED using functional update to avoid stale closure
      setTournament(prev => {
        if (!prev) {
          tournamentRef.current = prev
          return prev
        }
        if (prev.id !== data.tournamentId) {
          tournamentRef.current = prev
          return prev
        }
        const nextTournament = { ...prev, status: 'FINISHED' }
        tournamentRef.current = nextTournament
        return nextTournament
      })
    }
    
    const handleTournamentCancelled = (data: { tournamentId: string; reason?: string; message?: string; paidCount?: number; unpaidPlayers?: string[] }) => {
      console.log('❌ Tournament cancelled:', data)
      const reason = data.reason || data.message || 'Tournament cancelled'
      alert(`Tournament cancelled: ${reason}`)
      updateTournamentState(null)
      updateParticipantsState([])
      setFinalStandings(null)
    }
    
    const handleTournamentRoundStart = (data: { tournamentId: string; round: number; matches: string[][] }) => {
      console.log('[CLIENT] TournamentLobby: tournament_round_start received:', data)
      if (tournamentRef.current && tournamentRef.current.id !== data.tournamentId) {
        console.log('⏸️ AGC: Ignoring round start for unrelated tournament', data.tournamentId)
        return
      }
      
      // AGC: CRITICAL FIX - If tournament state is missing, request reconnect
      if (!tournamentRef.current && socket) {
        console.log('🏆 Tournament state missing for round start - requesting reconnect:', data.tournamentId)
        socket.emit('tournament_reconnect', { tournamentId: data.tournamentId })
      }
      
      // AGC: Update currentRound from event (but tournament object is source of truth)
      setCurrentRound(data.round)
      // AGC: Update rounds array
      setTournamentRounds(prev => {
        const newRounds = [...prev]
        // AGC: Replace or add the round
        const existingIndex = newRounds.findIndex(r => r.round === data.round)
        if (existingIndex >= 0) {
          newRounds[existingIndex] = { round: data.round, matches: data.matches }
        } else {
          newRounds.push({ round: data.round, matches: data.matches })
        }
        return newRounds.sort((a, b) => a.round - b.round)
      })
      
      // AGC: FIX - Clear completed matches for the new round to ensure "Play Match" button appears
      // AGC: This prevents stale completedMatches from hiding the button when returning from a battle
      if (address && tournamentRef.current) {
        setCompletedMatches(prev => {
          const newCompleted = { ...prev }
          // AGC: Clear matches for this round (they'll be re-marked as completed when actually finished)
          data.matches.forEach(match => {
            if (match[0] && match[1]) {
              // AGC: Generate matchId inline (same logic as generateMatchId function)
              const roundIndex = Math.max(0, data.round - 1)
              const [p1, p2] = [match[0].toLowerCase(), match[1].toLowerCase()].sort()
              const matchId = `match_${tournamentRef.current!.id}_${roundIndex}_${p1}_${p2}`
              
              // AGC: Only clear if this is a player's match
              const isPlayerMatch = match[0]?.toLowerCase() === address.toLowerCase() || 
                                   match[1]?.toLowerCase() === address.toLowerCase()
              if (isPlayerMatch && matchId) {
                delete newCompleted[matchId]
                console.log(`🔄 TournamentLobby: Cleared completed status for match ${matchId} (round ${data.round})`)
              }
            }
          })
          return newCompleted
        })
      }
    }
    
    const handleTournamentRoundsSync = (data: { tournamentId: string; rounds: TournamentRound[]; currentRound: number }) => {
      console.log('🏆 TournamentLobby: Received tournament_rounds_sync:', data)
      
      // AGC: Verify tournament ID matches
      if (tournamentRef.current && tournamentRef.current.id !== data.tournamentId) {
        console.log('⏸️ AGC: Ignoring rounds sync for unrelated tournament', data.tournamentId)
        return
      }
      
      // AGC: Update rounds array with all synced data
      setTournamentRounds(data.rounds.sort((a, b) => a.round - b.round))
      
      // AGC: Update current round
      setCurrentRound(data.currentRound)
      
      console.log(`✅ TournamentLobby: Synced ${data.rounds.length} rounds, currentRound: ${data.currentRound}`)
    }
    
    socket.on('tournament_created', handleTournamentCreated)
    socket.on('tournament_updated', handleTournamentUpdated)
    socket.on('tournament_reconnect', handleTournamentReconnect)
    socket.on('no_active_tournament', handleNoActiveTournament)
    socket.on('tournament_finished', handleTournamentFinished)
    socket.on('tournament_cancelled', handleTournamentCancelled)
    socket.on('tournament_round_start', handleTournamentRoundStart)
    socket.on('tournament_rounds_sync', handleTournamentRoundsSync) // AGC: FIX - Handle rounds sync
    const handleTournamentMatchResult = (data: { matchId?: string; winner?: string; loser?: string; isDraw?: boolean }) => {
      console.log('🏆 Match result updated:', data)
      if (data.matchId) {
        setCompletedMatches((prev) => {
          if (prev[data.matchId]) {
            return prev
          }
          return { ...prev, [data.matchId]: true }
        })
        
        // AGC: Store match result (winner/loser) for win/loss labels
        if (data.winner && data.loser) {
          setMatchResults((prev) => ({
            ...prev,
            [data.matchId]: {
              winner: data.winner || '',
              loser: data.loser || '',
              isDraw: data.isDraw || false
            }
          }))
        }
      }
    }

    socket.on('player_forfeited', (data) => {
      console.log('🏆 Player forfeited:', data)
      // AGC: Tournament state will be updated via tournament_updated event
    })
    socket.on('tournament_timeout_refund', (data) => {
      console.log('⏱️ Tournament timeout refund:', data)
      // AGC: Tournament will be marked as FINISHED
    })
    socket.on('tournament_match_result', handleTournamentMatchResult)
    
    // AGC: Check for tournament reconnection on mount (in case player disconnected)
    if (address) {
      socket.emit('check_tournament_reconnect', { playerAddress: address })
      
      // AGC: FIX - Request all tournament rounds to prevent missing Round 2 after battle
      socket.emit('request_tournament_rounds', { playerAddress: address })
    }
    
    // AGC: Request tournament on mount
    if (propTournamentId) {
      // AGC: Request specific tournament if ID provided
      socket.emit('get_tournament_by_id', { tournamentId: propTournamentId })
    } else {
      // AGC: Request current tournament (in case tournament was created before player connected)
      socket.emit('get_current_tournament')
    }
    
    return () => {
      socket.off('tournament_created', handleTournamentCreated)
      socket.off('tournament_updated', handleTournamentUpdated)
      socket.off('tournament_reconnect', handleTournamentReconnect)
      socket.off('no_active_tournament', handleNoActiveTournament)
      socket.off('tournament_finished', handleTournamentFinished)
      socket.off('tournament_cancelled', handleTournamentCancelled)
      socket.off('tournament_round_start', handleTournamentRoundStart)
      socket.off('tournament_rounds_sync', handleTournamentRoundsSync) // AGC: FIX - Clean up listener
      socket.off('player_forfeited')
      socket.off('tournament_timeout_refund')
      socket.off('tournament_match_result', handleTournamentMatchResult)
    }
  }, [socket, address, shouldApplyTournamentUpdate, updateTournamentState, updateParticipantsState, shouldIgnoreIncomingTournament]) // AGC: Added dependencies for stability
  
  // AGC: FIX - Refresh tournament rounds when current round changes (e.g., after completing a battle)
  // AGC: Uses refs to prevent infinite loop and debounce requests
  useEffect(() => {
    if (!socket || !address || !tournament || tournament.status !== 'RUNNING') return
    
    // AGC: Get the actual current round value
    const currentRoundValue = tournament.currentRound || currentRound
    
    // AGC: Only emit if round ACTUALLY changed (prevent infinite loop)
    if (currentRoundValue > 0 && currentRoundValue !== prevRoundRef.current) {
      // AGC: Debounce - don't request more than once per 2 seconds
      const now = Date.now()
      if (now - lastRoundRequestRef.current < 2000) {
        console.log(`⏳ TournamentLobby: Debouncing round request (${currentRoundValue})`)
        return
      }
      
      console.log(`🔄 TournamentLobby: Round changed from ${prevRoundRef.current} to ${currentRoundValue} - requesting fresh tournament rounds`)
      prevRoundRef.current = currentRoundValue
      lastRoundRequestRef.current = now
      
      socket.emit('request_tournament_rounds', { playerAddress: address })
      socket.emit('check_tournament_reconnect', { playerAddress: address })
    }
  }, [socket, address, tournament?.currentRound, currentRound, tournament?.status])
  
  // AGC: FIX - Refresh tournament state when component becomes visible (e.g., returning from battle)
  // AGC: Debounced to prevent flooding the backend
  useEffect(() => {
    if (!socket || !address || !tournament) return
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && tournament.status === 'RUNNING') {
        // AGC: Debounce - don't request more than once per 2 seconds
        const now = Date.now()
        if (now - lastRoundRequestRef.current < 2000) {
          console.log('⏳ TournamentLobby: Debouncing visibility refresh')
          return
        }
        
        console.log('🔄 TournamentLobby: Page became visible - refreshing tournament state')
        lastRoundRequestRef.current = now
        socket.emit('check_tournament_reconnect', { playerAddress: address })
        socket.emit('request_tournament_rounds', { playerAddress: address })
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [socket, address, tournament])
  
  // AGC: Play audio cue when 10 minute timer transitions into 2 minute ready phase
  useEffect(() => {
    if (!tournament) {
      prevStatusRef.current = null
      return
    }

    const previousStatus = prevStatusRef.current
    if (
      previousStatus &&
      previousStatus !== 'COUNTDOWN' &&
      tournament.status === 'COUNTDOWN' &&
      countdownAudioRef.current
    ) {
      console.log('🔊 TournamentLobby: Playing countdown-start audio cue')
      countdownAudioRef.current.currentTime = 0
      countdownAudioRef.current
        .play()
        .catch((error) => console.warn('⚠️ AGC: Failed to play tournament countdown audio cue', error))
    }

    if (
      previousStatus === 'COUNTDOWN' &&
      tournament.status === 'RUNNING' &&
      countdownAudioRef.current
    ) {
      console.log('🔊 TournamentLobby: Playing countdown-finished audio cue')
      countdownAudioRef.current.currentTime = 0
      countdownAudioRef.current
        .play()
        .catch((error) => console.warn('⚠️ AGC: Failed to play tournament start audio cue', error))
    }

    prevStatusRef.current = tournament.status
  }, [tournament])

  // AGC: Handle joining tournament (with payment)
  const handleJoinTournament = useCallback(async () => {
    if (!socket || !tournament || !address) {
      console.error('❌ TournamentLobby: Cannot join - missing requirements:', {
        socket: !!socket,
        tournament: !!tournament,
        address: !!address
      })
      return
    }

    setPaymentStatus('processing')
    setPaymentError(null)

    try {
      // AGC: Step 1: Pay entry fee on-chain
      console.log('🏆 TournamentLobby: Paying entry fee...')
      const txHash = await payEntry(tournament.id)

      if (!txHash) {
        setPaymentStatus('error')
        setPaymentError(paymentErrorHook || 'Payment failed')
        return
      }

      console.log('✅ TournamentLobby: Payment successful! TX:', txHash)
      setPaymentStatus('success')

      // AGC: Step 2: Emit payment confirmation to backend
      socket.emit('tournament_payment_confirmed', {
        tournamentId: tournament.id,
        playerAddress: address,
        txHash
      })

      // AGC: Step 3: Join tournament (backend will verify payment)
      const playerLevel = playerStats?.level || blockchainStats?.level || 1
      const totalBattles = blockchainStats?.totalBattles || 0
      const winRate = blockchainStats?.winRate || 0 // AGC: winRate is already a percentage (0-100)

      console.log('🏆 TournamentLobby: Joining tournament:', {
        tournamentId: tournament.id,
        playerAddress: address,
        playerLevel,
        totalBattles,
        winRate
      })

      socket.emit('join_tournament', {
        tournamentId: tournament.id,
        playerAddress: address,
        playerLevel,
        playerStats: {
          totalBattles,
          winRate
        }
      })

      // AGC: Reset payment status after a delay
      setTimeout(() => {
        setPaymentStatus('idle')
      }, 3000)
    } catch (error: any) {
      console.error('❌ TournamentLobby: Join error:', error)
      setPaymentStatus('error')
      setPaymentError(error.message || 'Failed to join tournament')
    }
  }, [socket, tournament, address, playerStats, blockchainStats, payEntry, paymentErrorHook])
  
  // AGC: Handle player ready
  const handlePlayerReady = useCallback(() => {
    if (!socket || !tournament || !address) return
    
    socket.emit('tournament_player_ready', {
      tournamentId: tournament.id,
      playerAddress: address
    })
  }, [socket, tournament, address])
  
  const generateMatchId = useCallback(
    (roundNumber: number, addr1?: string | null, addr2?: string | null) => {
      if (!tournament?.id || !roundNumber || !addr1 || !addr2) {
        return null
      }
      const roundIndex = Math.max(0, roundNumber - 1)
      const [p1, p2] = [addr1.toLowerCase(), addr2.toLowerCase()].sort()
      return `match_${tournament.id}_${roundIndex}_${p1}_${p2}`
    },
    [tournament?.id]
  )

  const getRoundMatchNumber = useCallback(
    (roundNumber: number, opponentAddr: string) => {
      if (!address || !roundNumber || !opponentAddr) {
        return 1
      }

      const round = tournamentRounds.find(r => r.round === roundNumber)
      if (!round) {
        return 1
      }

      const playerLower = address.toLowerCase()
      const opponentLower = opponentAddr.toLowerCase()
      const matchIndex = round.matches.findIndex(match => {
        const a = match[0]?.toLowerCase()
        const b = match[1]?.toLowerCase()
        return (
          (a === playerLower && b === opponentLower) ||
          (a === opponentLower && b === playerLower)
        )
      })

      return matchIndex >= 0 ? matchIndex + 1 : 1
    },
    [address, tournamentRounds]
  )

  const isMatchCompleted = useCallback((roundNumber: number, opponentAddr: string) => {
    if (!address || !opponentAddr) {
      return false
    }
    const matchId = generateMatchId(roundNumber, address, opponentAddr)
    return Boolean(matchId && completedMatches[matchId])
  }, [address, generateMatchId, completedMatches])

  const handleCopyTournamentId = useCallback(() => {
    if (!tournament?.id) {
      return
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(tournament.id)
        .then(() => {
          setCopiedTournamentId(true)
          setTimeout(() => setCopiedTournamentId(false), 1500)
        })
        .catch(() => {
          setCopiedTournamentId(false)
        })
    }
  }, [tournament])

  const formatTime = useCallback((ms: number) => {
    if (!Number.isFinite(ms) || ms <= 0) {
      return '0:00'
    }
    const minutes = Math.floor(ms / 60000)
    const seconds = Math.floor((ms % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }, [])
  
  // AGC: Calculate rounds and estimated time
  const calculateRoundsAndTime = () => {
    const n = participants.length
    if (n < 2) return { rounds: 0, totalMinutes: 0 }
    
    const rounds = n - 1
    const avgMatchTime = 5 // minutes per match
    const bufferTime = 1 // minutes buffer per round
    const totalMinutes = rounds * (avgMatchTime + bufferTime)
    
    return { rounds, totalMinutes }
  }
  
  // AGC: Get player's matches for a specific round (defaults to current round)
  const getPlayerMatches = useCallback((roundOverride?: number) => {
    if (!address || !tournamentRounds.length) return []
    
    const effectiveRound = roundOverride ?? tournament?.currentRound ?? currentRound
    if (!effectiveRound || effectiveRound <= 0) return []
    
    const round = tournamentRounds.find(r => r.round === effectiveRound)
    if (!round) {
      console.warn('🏆 TournamentLobby: No round found for round', effectiveRound, 'available rounds:', tournamentRounds.map(r => r.round))
      return []
    }
    
    const playerMatches = round.matches.filter(match => 
      match[0]?.toLowerCase() === address.toLowerCase() || 
      match[1]?.toLowerCase() === address.toLowerCase()
    )
    
    console.log('🏆 TournamentLobby: getPlayerMatches', {
      requestedRound: roundOverride,
      effectiveRound,
      tournamentCurrentRound: tournament?.currentRound,
      stateCurrentRound: currentRound,
      roundMatches: round.matches.length,
      playerMatches: playerMatches.length
    })
    
    return playerMatches
  }, [address, tournamentRounds, currentRound, tournament])
  
  // AGC: Handle starting a tournament match - navigate directly to PvPTourney (NFT selection happens there)
  const handleStartMatch = useCallback((opponentAddress: string) => {
    if (!socket || !tournament || !address) return
    
    // AGC: Get effective round from tournament object (source of truth) or state
    const effectiveRound = tournament.currentRound || currentRound
    if (effectiveRound === 0) {
      console.error('🏆 TournamentLobby: Cannot start match - no current round set')
      return
    }
    
    const matchNumber = getRoundMatchNumber(effectiveRound, opponentAddress)
    if (isMatchCompleted(effectiveRound, opponentAddress)) {
      console.log('🏆 TournamentLobby: Match already completed, waiting for next round')
      return
    }
    
    // AGC: Get opponent level from participants
    const opponent = participants.find(p => p.playerId.toLowerCase() === opponentAddress.toLowerCase())
    const opponentLevel = opponent?.level || 1

    console.log('🏆 Starting tournament match:', { 
      tournamentId: tournament.id, 
      opponentAddress,
      opponentLevel,
      round: effectiveRound,
      matchNumber,
      tournamentCurrentRound: tournament.currentRound,
      stateCurrentRound: currentRound
    })
    
    // AGC: Convert to zero-based round index for backend (tournamentManager stores rounds 0-indexed)
    const backendRoundIndex = Math.max(0, effectiveRound - 1)
    
    // AGC: Emit event to backend to create tournament match (no NFTs yet - will be selected in PvPTourney)
    // AGC: PROFESSIONAL TOURNAMENT ENGINE - Send all required fields for match lookup
    socket.emit('start_tournament_match', {
      tournamentId: tournament.id,
      player1Address: address, // AGC: For fallback lookup if socket not registered
      opponentAddress: opponentAddress, // AGC: Primary field name (matches backend expectation)
      player2Address: opponentAddress, // AGC: Legacy field name (backward compatibility)
      round: backendRoundIndex, // AGC: Backend requires 0-indexed rounds to find TournamentMatch objects
      roundIndex: backendRoundIndex, // AGC: Explicit flag to avoid future regressions
      roundNumber: effectiveRound, // AGC: Preserve 1-indexed value for analytics/logs
      selectedBuffs: [], // AGC: No buffs selected yet - will be selected in PvPTourney
      player1SelectedBuffs: [] // AGC: No buffs selected yet
    })
    
    // AGC: Dispatch event to MainMenu to navigate directly to tournament match page
    // AGC: NFT selection will happen in PvPTourney component
    // AGC: effectiveRound already declared above (line 454)
    window.dispatchEvent(new CustomEvent('tournament:start-match', {
        detail: {
          tournamentId: tournament.id,
          opponentAddress,
          opponentLevel,
          round: effectiveRound,
          matchNumber,
          selectedBuffs: [] // AGC: No pre-selected buffs - user will select in PvPTourney
        }
      }))
    }, [socket, tournament, address, currentRound, getPlayerMatches, participants, getRoundMatchNumber, isMatchCompleted])
  
  // AGC: Handle NFT selection confirmation - proceed with match creation
  const handleNFTSelectionConfirm = useCallback((selectedBuffs: number[]) => {
    if (!socket || !tournament || !address || !selectedMatch) return
    
    // AGC: Get opponent level from participants
    const opponent = participants.find(p => p.playerId.toLowerCase() === selectedMatch.opponentAddress.toLowerCase())
    const opponentLevel = opponent?.level || 1
    
    console.log('🏆 Starting tournament match with NFTs:', { 
      tournamentId: tournament.id, 
      opponentAddress: selectedMatch.opponentAddress,
      opponentLevel,
      round: selectedMatch.round,
      selectedBuffs 
    })
    
    // AGC: Close NFT selection modal
    setShowNFTSelection(false)
    setSelectedMatch(null)
    
    // AGC: Emit event to backend to create tournament match with selected NFTs
    // AGC: PROFESSIONAL TOURNAMENT ENGINE - Send all required fields for match lookup
    const backendRoundIndex = Math.max(0, (selectedMatch.round || 1) - 1)
    const derivedMatchNumber = selectedMatch.matchNumber || getRoundMatchNumber(selectedMatch.round, selectedMatch.opponentAddress)

    socket.emit('start_tournament_match', {
      tournamentId: tournament.id,
      player1Address: address, // AGC: For fallback lookup if socket not registered
      opponentAddress: selectedMatch.opponentAddress, // AGC: Primary field name (matches backend expectation)
      player2Address: selectedMatch.opponentAddress, // AGC: Legacy field name (backward compatibility)
      round: backendRoundIndex, // AGC: Backend requires zero-based rounds
      roundIndex: backendRoundIndex,
      roundNumber: selectedMatch.round,
      selectedBuffs: selectedBuffs, // AGC: Pass selected NFTs to backend
      player1SelectedBuffs: selectedBuffs // AGC: Alias for backend compatibility
    })
    
    // AGC: Dispatch event to MainMenu to navigate to tournament match page
    // AGC: This passes all necessary data for PvPTourney component
    window.dispatchEvent(new CustomEvent('tournament:start-match', {
      detail: {
        tournamentId: tournament.id,
        opponentAddress: selectedMatch.opponentAddress,
        opponentLevel,
        round: selectedMatch.round,
        matchNumber: derivedMatchNumber,
        selectedBuffs // AGC: Pass selected buffs for display (payment happens in PvPTourney)
      }
    }))
  }, [socket, tournament, address, selectedMatch, participants, onNavigateToPage, getRoundMatchNumber, isMatchCompleted])
  
  
  // AGC: Check if current player is in tournament
  const isPlayerInTournament = address && participants.some(p => p.playerId.toLowerCase() === address.toLowerCase())
  const currentPlayer = address ? participants.find(p => p.playerId.toLowerCase() === address.toLowerCase()) : null
  
  // AGC: Short address helper
  const shortAddress = (addr: string) => {
    if (!addr || addr.length < 10) return addr || 'Unknown'
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`
  }
  
  // AGC: Get participant info by address
  const getParticipantInfo = useCallback((addr: string) => {
    return participants.find(p => p.playerId.toLowerCase() === addr.toLowerCase())
  }, [participants])

  const playerMatches = useMemo(() => getPlayerMatches(), [getPlayerMatches])
  const effectiveRound = tournament?.currentRound || currentRound

  const playerMatchStatusList = useMemo(() => {
    if (!address) {
      return []
    }
    return playerMatches.map((match) => {
      const opponentAddr = match[0]?.toLowerCase() === address.toLowerCase() ? match[1] : match[0]
      const opponent = getParticipantInfo(opponentAddr || '')
      const matchNumber = getRoundMatchNumber(effectiveRound, opponentAddr || '')
      const matchId = generateMatchId(effectiveRound, address, opponentAddr || '')
      const completed = opponentAddr ? isMatchCompleted(effectiveRound, opponentAddr) : false
      const matchResult = matchId ? matchResults[matchId] : null
      
      // AGC: Determine win/loss for current player
      let playerOutcome: 'win' | 'loss' | 'draw' | null = null
      if (matchResult && address) {
        const playerAddr = address.toLowerCase()
        if (matchResult.isDraw) {
          playerOutcome = 'draw'
        } else if (matchResult.winner?.toLowerCase() === playerAddr) {
          playerOutcome = 'win'
        } else if (matchResult.loser?.toLowerCase() === playerAddr) {
          playerOutcome = 'loss'
        }
      }
      
      return { 
        opponentAddr, 
        opponent, 
        matchNumber, 
        completed, 
        roundNumber: effectiveRound,
        matchId,
        playerOutcome,
        matchResult
      }
    })
  }, [playerMatches, address, getParticipantInfo, getRoundMatchNumber, isMatchCompleted, effectiveRound, generateMatchId, matchResults])

  const playerMatchesCompleted =
    playerMatchStatusList.length > 0 &&
    playerMatchStatusList.every((match) => match.completed)

  // AGC: Check if current round is fully complete (all matches in round, not just player's matches)
  const isCurrentRoundFullyComplete = useCallback(() => {
    if (!tournament || !effectiveRound || effectiveRound <= 0) return false
    
    const round = tournamentRounds.find(r => r.round === effectiveRound)
    if (!round) return false
    
    // AGC: Check if all matches in the current round are completed
    const allMatchesInRound = round.matches
    const allMatchesCompleted = allMatchesInRound.every((match) => {
      if (!match[0] || !match[1]) return true // AGC: Skip bye matches
      const matchId = generateMatchId(effectiveRound, match[0], match[1])
      return matchId ? completedMatches[matchId] : false
    })
    
    return allMatchesCompleted
  }, [tournament, effectiveRound, tournamentRounds, completedMatches, generateMatchId])

  const currentRoundFullyComplete = isCurrentRoundFullyComplete()
  
  const waitingForNextRound = Boolean(
    isPlayerInTournament &&
    effectiveRound > 0 &&
    tournament?.status === 'RUNNING' &&
    playerMatchesCompleted &&
    !currentRoundFullyComplete // AGC: Only wait if current round is NOT fully complete
  )

  const nextRoundNumber = effectiveRound > 0 ? effectiveRound + 1 : 0
  const nextRoundPlayerMatches = useMemo(() => {
    if (!address || !nextRoundNumber) {
      return []
    }
    const nextRoundMatches = getPlayerMatches(nextRoundNumber)
    if (!nextRoundMatches.length) {
      return []
    }
    return nextRoundMatches.map((match) => {
      const opponentAddr = match[0]?.toLowerCase() === address.toLowerCase() ? match[1] : match[0]
      const opponent = getParticipantInfo(opponentAddr || '')
      return {
        opponentAddr,
        opponent,
        matchNumber: getRoundMatchNumber(nextRoundNumber, opponentAddr || ''),
        roundNumber: nextRoundNumber
      }
    })
  }, [address, nextRoundNumber, getPlayerMatches, getParticipantInfo, getRoundMatchNumber])

  const isPlayerWaitingForNextRound = Boolean(
    waitingForNextRound && nextRoundPlayerMatches.length > 0
  )

  const displayRoundNumber = isPlayerWaitingForNextRound ? nextRoundNumber : effectiveRound

  const matchCards = useMemo(() => {
    if (isPlayerWaitingForNextRound) {
      // AGC: Show next round's matches as waiting (locked until current round completes)
      return nextRoundPlayerMatches.map((match) => ({
        ...match,
        completed: false,
        waiting: true,
        playerOutcome: null,
        matchResult: null
      }))
    }
    // AGC: Show current round's matches
    return playerMatchStatusList.map((match) => ({
      ...match,
      waiting: false
    }))
  }, [isPlayerWaitingForNextRound, nextRoundPlayerMatches, playerMatchStatusList])
  
  if (!tournament) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
        <p className="text-slate-400 text-sm">Loading tournament...</p>
      </div>
    )
  }
  
  const { rounds, totalMinutes } = calculateRoundsAndTime()
  const readyCount = participants.filter(p => p.isReady).length
  
  return (
    <div className="w-full">
      {/* AGC: Hero Header - No card background, border divider only */}
      <div className="pb-3 border-b border-white/10 mb-4">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-6 h-6 text-yellow-400" />
          <h2 className="text-xl font-bold text-white">Round-Robin Contest</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
          <span className="uppercase tracking-[0.3em] text-slate-400">Tournament ID</span>
          <button
            onClick={handleCopyTournamentId}
            className="flex items-center gap-2 px-2 py-1 bg-slate-800/50 border border-white/10 rounded hover:border-emerald-400/70 transition-colors"
            title={tournament.id}
          >
            <code className="text-[11px] font-mono text-white">
              {shortAddress(tournament.id)}
            </code>
            <span className={`text-[10px] ${copiedTournamentId ? 'text-emerald-400' : 'text-slate-400'}`}>
              {copiedTournamentId ? 'Copied!' : 'Copy'}
            </span>
          </button>
          {(tournament.status === 'WAITING_FOR_PLAYERS' || tournament.status === 'COUNTDOWN') && (
            <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-400 bg-slate-800/60 border border-emerald-500/40 rounded-full px-2 py-1">
              <span>⏱️</span>
              <span>{formatTime(timeRemaining)}</span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-slate-300">
                {tournament.status === 'WAITING_FOR_PLAYERS' ? 'Auto-start' : 'Ready Phase'}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 mt-2">
          <span>👥 {participants.length}/{tournament.maxPlayers}</span>
          {tournament.status === 'WAITING_FOR_PLAYERS' && (
            <span>Needs {tournament.minPlayersToStart}+ players to auto-start</span>
          )}
          {tournament.status === 'COUNTDOWN' && (
            <span>⏳ Countdown active{isPlayerInTournament ? ' (see top bar)' : ''}</span>
          )}
          {tournament.status === 'RUNNING' && <span>🔥 In Progress</span>}
          {tournament.status === 'FINISHED' && <span>🏁 Finished</span>}
          {(tournament.status === 'COUNTDOWN' || tournament.status === 'RUNNING') && (
            <span>✅ {readyCount}/{participants.length} ready</span>
          )}
        </div>
      </div>
      
      {/* AGC: Format & Rewards - Compact 2-column grid, no card backgrounds */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Format Info */}
        <div className="pb-2 border-b border-white/10">
          <div className="flex items-center gap-1.5 mb-1">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[9px] uppercase text-slate-400 tracking-wide">Format</span>
          </div>
          <div className="text-xs text-white space-y-0.5">
            <div>Round Robin</div>
            <div className="text-slate-400">{rounds} rounds • ~{totalMinutes}min</div>
          </div>
        </div>
        
        {/* Rewards */}
        <div className="pb-2 border-b border-white/10">
          <div className="flex items-center gap-1.5 mb-1">
            <Award className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[9px] uppercase text-slate-400 tracking-wide">Prizes</span>
          </div>
          <div className="text-xs text-white space-y-0.5">
            <div>🥇 {tournament.rewards.first.ogb} OGB</div>
            <div className="text-slate-400">🥈 {tournament.rewards.second.ogb} • 🥉 {tournament.rewards.third.ogb}</div>
          </div>
        </div>
      </div>
      
      {/* AGC: Participants List - Compact, no card background */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2 pb-1 border-b border-white/10">
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[9px] uppercase text-slate-400 tracking-wide">Players</span>
          </div>
          {tournament.status === 'COUNTDOWN' && (
            <span className="text-[10px] text-slate-400">{readyCount}/{participants.length} ready</span>
          )}
        </div>
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {participants.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-3">No players joined yet</p>
          ) : (
            participants.map((p) => (
              <div 
                key={p.playerId} 
                className={`flex items-center justify-between py-1.5 px-2 rounded ${
                  p.playerId.toLowerCase() === address?.toLowerCase() ? 'bg-purple-500/20 ring-1 ring-purple-500/50' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="text-xs font-medium text-white">
                    {shortAddress(p.playerId)}
                    {p.playerId.toLowerCase() === address?.toLowerCase() && (
                      <span className="ml-1.5 text-[10px] text-purple-400">(You)</span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    L{p.level} • {p.totalBattles}⚔ • {typeof p.winRate === 'number' ? (p.winRate > 1 ? p.winRate.toFixed(0) : (p.winRate * 100).toFixed(0)) : '0'}%
                  </div>
                </div>
                {tournament.status === 'COUNTDOWN' && (
                  <div className="text-[10px]">
                    {p.isReady ? '✅' : '⏳'}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* AGC: Final Standings Display */}
      {tournament.status === 'FINISHED' && finalStandings && (
        <div className="mb-4 pb-3 border-b border-white/10">
          <div className="flex items-center gap-1.5 mb-2">
            <Trophy className="w-3.5 h-3.5 text-yellow-400" />
            <span className="text-[9px] uppercase text-slate-400 tracking-wide">Final Results</span>
          </div>
          <div className="space-y-1.5">
            {finalStandings.slice(0, 3).map((standing, index) => (
              <div 
                key={standing.address} 
                className="flex items-center justify-between py-1.5 px-2 rounded bg-slate-800/30"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                  </span>
                  <span className="text-xs font-medium text-white">
                    {shortAddress(standing.address)}
                    {standing.address.toLowerCase() === address?.toLowerCase() && (
                      <span className="ml-1.5 text-[10px] text-purple-400">(You)</span>
                    )}
                  </span>
                </div>
                <div className="text-[10px] text-slate-400">
                  {standing.wins}W - {standing.losses}L{standing.draws > 0 ? ` - ${standing.draws}D` : ''} ({standing.wins + standing.losses + (standing.draws || 0)} games)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AGC: Primary Action Button - Always visible, game-style */}
      <div className="pt-3 border-t border-white/10">
        {tournament.status === 'WAITING_FOR_PLAYERS' && !isPlayerInTournament && (
          <>
            <button
              onClick={handleJoinTournament}
              disabled={isPaymentLoading || paymentStatus === 'processing'}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-all active:scale-95 shadow-lg shadow-purple-500/30 border border-purple-400/30"
            >
              <div className="flex items-center justify-center gap-2">
                {isPaymentLoading || paymentStatus === 'processing' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    <span>Processing payment...</span>
                  </>
                ) : paymentStatus === 'success' ? (
                  <>
                    <span>✅</span>
                    <span>Joined!</span>
                  </>
                ) : (
                  <>
                    <Trophy className="w-5 h-5" />
                    <span>Join Contest (50 OGB)</span>
                  </>
                )}
              </div>
            </button>
            {paymentStatus === 'error' && paymentError && (
              <div className="mt-2 text-xs text-red-400 text-center">
                {paymentError}
              </div>
            )}
          </>
        )}
        
        {tournament.status === 'COUNTDOWN' && isPlayerInTournament && !currentPlayer?.isReady && (
          <button
            onClick={handlePlayerReady}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-3 px-4 rounded-lg transition-all active:scale-95 shadow-lg shadow-emerald-500/30 border border-emerald-400/30"
          >
            <div className="flex items-center justify-center gap-2">
              <span>✅</span>
              <span>I'm Ready!</span>
            </div>
          </button>
        )}
        
        {tournament.status === 'COUNTDOWN' && currentPlayer?.isReady && (
          <div className="w-full bg-emerald-600/20 border border-emerald-500/50 text-emerald-400 font-semibold py-3 px-4 rounded-lg text-center">
            ✅ Ready - Waiting for others...
          </div>
        )}
        
        {tournament.status === 'RUNNING' && (
          <div className="space-y-4">
            {/* AGC: Current Round Info */}
            {displayRoundNumber > 0 && (
              <div className="mb-4 pb-3 border-b border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Play className="w-3.5 h-3.5 text-orange-400" />
                    <span className="text-[9px] uppercase text-slate-400 tracking-wide">
                      Round {displayRoundNumber}
                      {isPlayerWaitingForNextRound && (
                        <span className="ml-1 text-[10px] text-yellow-300">(waiting to start)</span>
                      )}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {tournamentRounds.length > 0 && `${Math.min(displayRoundNumber, tournamentRounds.length)}/${tournamentRounds.length}`}
                  </span>
                </div>
                
                {/* AGC: Player's Matches */}
                {isPlayerInTournament && (
                  <div className="space-y-2">
                    {matchCards.length > 0 ? (
                      matchCards.map((match, idx) => (
                        <button
                          key={`${match.opponentAddr}-${idx}`}
                          onClick={() => handleStartMatch(match.opponentAddr || '')}
                          disabled={match.completed || match.waiting}
                          className={`w-full bg-gradient-to-r from-orange-600 to-red-600 text-white font-bold py-3 px-4 rounded-lg transition-all active:scale-95 shadow-lg shadow-orange-500/30 border border-orange-400/30 ${
                            (match.completed || match.waiting)
                              ? 'opacity-70 cursor-not-allowed hover:from-orange-600 hover:to-red-600'
                              : 'hover:from-orange-700 hover:to-red-700'
                          } ${
                            match.playerOutcome === 'win' ? 'ring-2 ring-emerald-400/50' :
                            match.playerOutcome === 'loss' ? 'ring-2 ring-red-400/50' :
                            match.playerOutcome === 'draw' ? 'ring-2 ring-yellow-400/50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {match.completed ? (
                                match.playerOutcome === 'win' ? (
                                  <span className="text-emerald-300 text-lg">✓</span>
                                ) : match.playerOutcome === 'loss' ? (
                                  <span className="text-red-300 text-lg">✗</span>
                                ) : match.playerOutcome === 'draw' ? (
                                  <span className="text-yellow-300 text-lg">=</span>
                                ) : (
                                  <Play className="w-4 h-4" />
                                )
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                              <span>
                                {match.waiting
                                  ? 'Waiting for round to unlock'
                                  : match.completed
                                    ? match.playerOutcome === 'win' 
                                      ? 'Match Won ✓'
                                      : match.playerOutcome === 'loss'
                                      ? 'Match Lost ✗'
                                      : match.playerOutcome === 'draw'
                                      ? 'Match Draw ='
                                      : 'Match Completed'
                                    : 'Play Match'}
                              </span>
                            </div>
                            <div className="text-xs text-orange-100 flex items-center gap-2">
                              <span>
                                Match {match.matchNumber} • vs {shortAddress(match.opponentAddr || '')}{' '}
                                {match.opponent && `(L${match.opponent.level})`}
                              </span>
                              {match.completed && (
                                <>
                                  {match.playerOutcome === 'win' && (
                                    <span className="text-emerald-300 text-[11px] font-bold">✓ WIN</span>
                                  )}
                                  {match.playerOutcome === 'loss' && (
                                    <span className="text-red-300 text-[11px] font-bold">✗ LOSS</span>
                                  )}
                                  {match.playerOutcome === 'draw' && (
                                    <span className="text-yellow-300 text-[11px] font-bold">= DRAW</span>
                                  )}
                                  {!match.playerOutcome && (
                                    <span className="text-emerald-300 text-[11px]">✅</span>
                                  )}
                                </>
                              )}
                              {match.waiting && <span className="text-yellow-300 text-[11px]">⏳</span>}
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="text-xs text-slate-400 text-center py-2">
                        No matches in this round
                      </div>
                    )}
                  </div>
                )}

                {isPlayerWaitingForNextRound && (
                  <div className="mt-3 text-xs text-slate-300 bg-slate-800/40 border border-slate-700 rounded px-3 py-2">
                    ✅ All your matches for Round {effectiveRound} are finished. Round {nextRoundNumber} will unlock once remaining battles conclude.
                  </div>
                )}

                {isPlayerWaitingForNextRound && (
                  <div className="mt-3 p-3 rounded border border-indigo-500/40 bg-indigo-900/20 space-y-2">
                    <div className="text-[10px] uppercase text-indigo-300 tracking-[0.2em]">Next Round Preview</div>
                    <ul className="space-y-1 text-xs text-white/80">
                      {nextRoundPlayerMatches.map((match, idx) => (
                        <li key={`${match.opponentAddr}-${idx}`} className="flex items-center justify-between">
                          <span>Match {match.matchNumber} • vs {shortAddress(match.opponentAddr || '')} {match.opponent && `(L${match.opponent.level})`}</span>
                          <span className="text-yellow-300 text-[10px]">Waiting…</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      className="w-full text-[11px] font-semibold text-white/70 border border-white/20 rounded py-2 cursor-not-allowed"
                      disabled
                    >
                      Waiting for previous round to finish
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* AGC: All Rounds Overview */}
            {tournamentRounds.length > 0 && (
              <div className="mb-4 pb-3 border-b border-white/10">
                <div className="flex items-center gap-1.5 mb-2">
                  <Trophy className="w-3.5 h-3.5 text-yellow-400" />
                  <span className="text-[9px] uppercase text-slate-400 tracking-wide">Tournament Bracket</span>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {tournamentRounds.map((round) => (
                    <div key={round.round} className="text-xs">
                      <div className="font-semibold text-white mb-1">
                        Round {round.round}
                      </div>
                      <div className="space-y-1 pl-2">
                        {round.matches.map((match, idx) => {
                          const p1 = getParticipantInfo(match[0] || '')
                          const p2 = getParticipantInfo(match[1] || '')
                          const matchId = generateMatchId(round.round, match[0], match[1])
                          const completed = matchId ? completedMatches[matchId] : false
                          const matchResult = matchId ? matchResults[matchId] : null
                          const isPlayerMatch = address && (
                            match[0]?.toLowerCase() === address.toLowerCase() || 
                            match[1]?.toLowerCase() === address.toLowerCase()
                          )
                          
                          // AGC: Determine win/loss for current player
                          let playerOutcome: 'win' | 'loss' | 'draw' | null = null
                          if (isPlayerMatch && matchResult && address) {
                            const playerAddr = address.toLowerCase()
                            if (matchResult.isDraw) {
                              playerOutcome = 'draw'
                            } else if (matchResult.winner?.toLowerCase() === playerAddr) {
                              playerOutcome = 'win'
                            } else if (matchResult.loser?.toLowerCase() === playerAddr) {
                              playerOutcome = 'loss'
                            }
                          }
                          
                          return (
                            <div 
                              key={idx}
                              className={`text-[10px] py-1.5 px-2 rounded ${
                                isPlayerMatch ? 'bg-purple-500/20 ring-1 ring-purple-500/50' : 'bg-slate-800/30'
                              } ${completed ? 'opacity-75' : ''}`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <div className="flex items-center gap-1 flex-1 min-w-0">
                                  <span className={`${isPlayerMatch ? 'text-purple-300' : 'text-slate-300'} truncate ${
                                    matchResult && matchResult.winner?.toLowerCase() === match[0]?.toLowerCase() 
                                      ? 'font-semibold text-emerald-300' 
                                      : matchResult && matchResult.loser?.toLowerCase() === match[0]?.toLowerCase()
                                      ? 'text-red-400 line-through'
                                      : ''
                                  }`}>
                                    {shortAddress(match[0] || '')} {p1 && `(L${p1.level})`}
                                  </span>
                                  {/* AGC: Show WIN/LOSS/DRAW for all matches, not just player's matches */}
                                  {matchResult && !matchResult.isDraw && matchResult.winner?.toLowerCase() === match[0]?.toLowerCase() && (
                                    <span className="text-emerald-400 text-[9px] font-bold">✓ WIN</span>
                                  )}
                                  {matchResult && !matchResult.isDraw && matchResult.loser?.toLowerCase() === match[0]?.toLowerCase() && (
                                    <span className="text-red-400 text-[9px] font-bold">✗ LOSS</span>
                                  )}
                                  {matchResult?.isDraw && (
                                    <span className="text-yellow-400 text-[9px] font-bold">= DRAW</span>
                                  )}
                                </div>
                                <span className="text-slate-500 mx-1">vs</span>
                                <div className="flex items-center gap-1 flex-1 min-w-0 justify-end">
                                  {/* AGC: Show WIN/LOSS/DRAW for all matches, not just player's matches */}
                                  {matchResult && !matchResult.isDraw && matchResult.winner?.toLowerCase() === match[1]?.toLowerCase() && (
                                    <span className="text-emerald-400 text-[9px] font-bold">✓ WIN</span>
                                  )}
                                  {matchResult && !matchResult.isDraw && matchResult.loser?.toLowerCase() === match[1]?.toLowerCase() && (
                                    <span className="text-red-400 text-[9px] font-bold">✗ LOSS</span>
                                  )}
                                  {matchResult?.isDraw && (
                                    <span className="text-yellow-400 text-[9px] font-bold">= DRAW</span>
                                  )}
                                  <span className={`${isPlayerMatch ? 'text-purple-300' : 'text-slate-300'} truncate ${
                                    matchResult && matchResult.winner?.toLowerCase() === match[1]?.toLowerCase() 
                                      ? 'font-semibold text-emerald-300' 
                                      : matchResult && matchResult.loser?.toLowerCase() === match[1]?.toLowerCase()
                                      ? 'text-red-400 line-through'
                                      : ''
                                  }`}>
                                    {shortAddress(match[1] || '')} {p2 && `(L${p2.level})`}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                  {completed && (
                                    <span className="text-emerald-400 text-[9px]" title="Match completed">✓</span>
                                  )}
                                  {!completed && (
                                    <span className="text-slate-500 text-[9px]">…</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {displayRoundNumber === 0 && (
              <div className="w-full bg-orange-600/20 border border-orange-500/50 text-orange-400 font-semibold py-3 px-4 rounded-lg text-center">
                🔥 Tournament in Progress - Waiting for matches...
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* AGC: NFT Selection Modal */}
      {showNFTSelection && selectedMatch && address && (
        <TournamentNFTSelection
          isOpen={showNFTSelection}
          onClose={() => {
            setShowNFTSelection(false)
            setSelectedMatch(null)
          }}
          onConfirm={handleNFTSelectionConfirm}
          playerAddress={address}
          opponentAddress={selectedMatch.opponentAddress}
          round={selectedMatch.round}
          matchNumber={selectedMatch.matchNumber}
          onNavigateToMarketplace={onNavigateToPage ? () => onNavigateToPage('games') : undefined}
        />
      )}
    </div>
  )
}

